// Цифровые UGC-артикулы (ТЗ 31.10.2026): покупательская часть и Единый
// партнёрский кабинет. Схемы - из общего контракта
// `@osmix/contracts/ugc-articles`, здесь только http-реализация и мок. В `Api`
// это `api.ugc.*`: резолв кода из поиска, переход по артикулу и ручки
// партнёра (/partner/*).
import {
	UGC_ATTRIBUTION_DAYS,
	UgcAccessRequestSchema,
	UgcAnalyticsSchema,
	UgcArticleSchema,
	UgcCatalogItemSchema,
	UgcCommissionSchema,
	UgcPartnerProfileSchema,
	UgcResolveResultSchema,
	UgcVisitResultSchema,
	looksLikeUgcArticle,
	type UgcAccessRequest,
	type UgcAnalytics,
	type UgcAnalyticsQuery,
	type UgcArticle,
	type UgcArticleInput,
	type UgcArticlePatch,
	type UgcArticleStatus,
	type UgcBulkInput,
	type UgcCatalogItem,
	type UgcCatalogQuery,
	type UgcChannel,
	type UgcCommission,
	type UgcCommissionState,
	type UgcMetrics,
	type UgcPartnerJoinInput,
	type UgcPartnerProfile,
	type UgcResolveResult,
	type UgcVisitInput,
	type UgcVisitResult,
} from "@osmix/contracts/ugc-articles";

import type { Product } from "./types";

export type {
	UgcAccessRequest,
	UgcAnalytics,
	UgcAnalyticsQuery,
	UgcAnalyticsRow,
	UgcArticle,
	UgcArticleInput,
	UgcArticlePatch,
	UgcArticleStatus,
	UgcBalance,
	UgcBulkInput,
	UgcCatalogItem,
	UgcCatalogQuery,
	UgcChannel,
	UgcCommission,
	UgcCommissionState,
	UgcMetrics,
	UgcPartnerJoinInput,
	UgcPartnerProfile,
	UgcPartnerRole,
	UgcResolveResult,
	UgcStoredAttribution,
	UgcVisitResult,
} from "@osmix/contracts/ugc-articles";

export type UgcCatalogPage = { items: UgcCatalogItem[]; total: number };
export type UgcCatalogFilter = Partial<Omit<UgcCatalogQuery, "sort">> & { sort?: UgcCatalogQuery["sort"] };
export type UgcStatsFilter = Partial<Omit<UgcAnalyticsQuery, "groupBy">> & { groupBy?: UgcAnalyticsQuery["groupBy"] };

export type UgcApi = {
	/** Код из поиска: активный артикул - карточка товара, иначе причина. */
	resolve(code: string): Promise<UgcResolveResult>;
	/** Переход по артикулу с этого устройства (окно атрибуции 30 дней). */
	visit(input: UgcVisitInput): Promise<UgcVisitResult>;
	partner: {
		me(): Promise<UgcPartnerProfile | null>;
		join(input: UgcPartnerJoinInput): Promise<UgcPartnerProfile>;
		update(patch: Partial<UgcPartnerJoinInput>): Promise<UgcPartnerProfile>;
		catalog(query?: UgcCatalogFilter): Promise<UgcCatalogPage>;
		articles(query?: { productId?: string; status?: UgcArticleStatus }): Promise<UgcArticle[]>;
		createArticle(input: UgcArticleInput): Promise<UgcArticle>;
		bulk(input: UgcBulkInput): Promise<UgcArticle[]>;
		updateArticle(id: string, patch: UgcArticlePatch): Promise<UgcArticle>;
		requestAccess(productId: string, message?: string): Promise<UgcAccessRequest>;
		stats(query?: UgcStatsFilter): Promise<UgcAnalytics>;
		commissions(state?: UgcCommissionState): Promise<UgcCommission[]>;
	};
};

type Requester = <T>(path: string, opts?: { method?: string; body?: unknown; auth?: boolean }) => Promise<T>;

const enc = encodeURIComponent;

/** Схема контракта - другой мажор zod, поэтому типизированная обёртка. */
type Parser<T> = { safeParse(raw: unknown): { success: true; data: T } | { success: false } };

/** Элементы списка - поштучно: один кривой не уносит весь раздел кабинета. */
const parseEach = <T>(schema: Parser<T>, raw: unknown): T[] =>
	Array.isArray(raw)
		? raw.flatMap((entry) => {
				const parsed = schema.safeParse(entry);
				return parsed.success ? [parsed.data] : [];
			})
		: [];

const qs = (params: Record<string, string | number | undefined | null>): string => {
	const p = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) if (v != null && v !== "") p.set(k, String(v));
	const s = p.toString();
	return s ? `?${s}` : "";
};

export const createUgcHttp = (request: Requester): UgcApi => ({
	resolve: async (code) =>
		UgcResolveResultSchema.parse(await request(`/articles/resolve/${enc(code.trim())}`)) as UgcResolveResult,
	visit: async (input) =>
		UgcVisitResultSchema.parse(await request("/articles/visit", { method: "POST", body: input })) as UgcVisitResult,
	partner: {
		me: async () => {
			const raw = await request<unknown>("/partner/me");
			return raw == null ? null : (UgcPartnerProfileSchema.parse(raw) as UgcPartnerProfile);
		},
		join: async (input) =>
			UgcPartnerProfileSchema.parse(await request("/partner/join", { method: "POST", body: input })) as UgcPartnerProfile,
		update: async (patch) =>
			UgcPartnerProfileSchema.parse(await request("/partner/me", { method: "PATCH", body: patch })) as UgcPartnerProfile,
		catalog: async (query = {}) => {
			const raw = await request<{ items?: unknown; total?: number }>(
				`/partner/catalog${qs({
					q: query.q,
					categoryId: query.categoryId,
					minCommission: query.minCommission,
					sort: query.sort,
					page: query.page,
					pageSize: query.pageSize,
				})}`,
			);
			const items = parseEach<UgcCatalogItem>(UgcCatalogItemSchema, raw?.items);
			return { items, total: typeof raw?.total === "number" ? raw.total : items.length };
		},
		articles: async (query = {}) =>
			parseEach<UgcArticle>(UgcArticleSchema, await request(`/partner/articles${qs(query)}`)),
		createArticle: async (input) =>
			UgcArticleSchema.parse(await request("/partner/articles", { method: "POST", body: input })) as UgcArticle,
		bulk: async (input) =>
			parseEach<UgcArticle>(UgcArticleSchema, await request("/partner/articles/bulk", { method: "POST", body: input })),
		updateArticle: async (id, patch) =>
			UgcArticleSchema.parse(await request(`/partner/articles/${enc(id)}`, { method: "PATCH", body: patch })) as UgcArticle,
		requestAccess: async (productId, message) =>
			UgcAccessRequestSchema.parse(
				await request("/partner/access-requests", { method: "POST", body: { productId, message } }),
			) as UgcAccessRequest,
		stats: async (query = {}) =>
			UgcAnalyticsSchema.parse(
				await request(
					`/partner/stats${qs({
						from: query.from,
						to: query.to,
						days: query.days,
						groupBy: query.groupBy,
						productId: query.productId,
						partnerId: query.partnerId,
					})}`,
				),
			) as UgcAnalytics,
		commissions: async (state) =>
			parseEach<UgcCommission>(UgcCommissionSchema, await request(`/partner/commissions${qs({ state })}`)),
	},
});

// ── Мок: витрина без шлюза ──────────────────────────────────────────────────
// Реестр артикулов в памяти вкладки. Правила - как у шлюза по контракту:
// 8 цифр, первая не 0; доступ к товару - «Доступно всем» или одобренная заявка;
// не больше 50 источников за раз.
const DAY = 86_400_000;
const iso = (ms: number) => new Date(ms).toISOString();
const delay = (ms = 160) => new Promise((r) => setTimeout(r, ms));

const zeroMetrics = (): UgcMetrics => ({
	clicks: 0,
	orders: 0,
	paidOrders: 0,
	completedOrders: 0,
	returns: 0,
	cr: 0,
	gmv: 0,
	commission: 0,
});

const sumMetrics = (list: UgcMetrics[]): UgcMetrics => {
	const t = list.reduce(
		(acc, m) => ({
			clicks: acc.clicks + m.clicks,
			orders: acc.orders + m.orders,
			paidOrders: acc.paidOrders + m.paidOrders,
			completedOrders: acc.completedOrders + m.completedOrders,
			returns: acc.returns + m.returns,
			cr: 0,
			gmv: acc.gmv + m.gmv,
			commission: acc.commission + m.commission,
		}),
		zeroMetrics(),
	);
	return { ...t, cr: t.clicks ? Math.round((t.orders / t.clicks) * 1000) / 10 : 0 };
};

export const createUgcMock = (
	products: () => Product[],
	shopName: (shopId: string) => string,
	signedIn: () => boolean,
): UgcApi & { resolveSync(code: string): UgcResolveResult } => {
	let profile: UgcPartnerProfile | null = null;
	const articles: UgcArticle[] = [];
	const access = new Map<string, UgcCatalogItem["access"]>();
	const used = new Set<string>();

	const pool = () => products().filter((p) => p.photos.length > 0).slice(0, 24);
	const percentOf = (index: number) => [12, 8, 15, 10, 5, 20][index % 6] ?? 10;
	const accessOf = (productId: string, index: number): UgcCatalogItem["access"] =>
		access.get(productId) ?? (index % 4 === 3 ? "requestable" : "open");

	const nextCode = (): string => {
		for (;;) {
			const code = String(10_000_000 + Math.floor(Math.random() * 89_999_999));
			if (!used.has(code)) {
				used.add(code);
				return code;
			}
		}
	};

	const requirePartner = (): UgcPartnerProfile => {
		if (!signedIn()) throw new Error("Sign in required");
		if (!profile) throw new Error("Сначала вступите в партнёрскую программу");
		return profile;
	};

	const makeArticle = (product: Product, index: number, input: Omit<UgcArticleInput, "productId">): UgcArticle => {
		const me = requirePartner();
		const now = iso(Date.now());
		const article: UgcArticle = {
			id: `ugc_${Math.random().toString(36).slice(2, 10)}`,
			code: nextCode(),
			productId: product.id,
			productTitle: product.name,
			productImage: product.photos[0] ?? null,
			shopId: product.shopId,
			shopName: shopName(product.shopId),
			ownerKind: "partner",
			partnerId: me.partnerId,
			partnerName: me.displayName,
			partnerRole: me.role,
			name: input.name,
			placement: input.placement ?? "",
			campaign: input.campaign ?? "",
			channel: input.channel ?? "other",
			status: "active",
			commissionPercent: percentOf(index),
			statusReason: null,
			metrics: zeroMetrics(),
			createdAt: now,
			updatedAt: now,
		};
		articles.unshift(article);
		return article;
	};

	const productFor = (productId: string): { product: Product; index: number } => {
		const list = pool();
		const index = list.findIndex((p) => p.id === productId);
		const product = list[index];
		if (!product) throw new Error("Товар не участвует в партнёрской программе");
		const state = accessOf(productId, index);
		if (state !== "open" && state !== "approved") {
			throw new Error("Продавец ограничил возможность генерации артикулов для этого товара. Отправьте запрос продавцу");
		}
		return { product, index };
	};

	const resolveSync = (raw: string): UgcResolveResult => {
		const code = raw.trim();
		if (!looksLikeUgcArticle(code)) {
			return { found: false, code, reason: "invalid", message: "Артикул должен состоять только из цифр и быть уникальным в системе" };
		}
		const article = articles.find((a) => a.code === code);
		if (!article) return { found: false, code, reason: "not_found", message: "Введённый артикул недействителен или был отключён владельцем" };
		if (article.status !== "active") {
			return { found: false, code, reason: "inactive", message: "Введённый артикул недействителен или был отключён владельцем" };
		}
		return {
			found: true,
			code,
			articleId: article.id,
			productId: article.productId,
			productPath: `/product/${article.productId}`,
			shopId: article.shopId,
			partnerId: article.partnerId,
		};
	};

	return {
		resolveSync,
		resolve: async (code) => {
			await delay(90);
			return resolveSync(code);
		},
		visit: async (input) => {
			await delay(60);
			const article = articles.find((a) => a.code === input.code && a.status === "active");
			if (!article) return { ok: false, code: input.code, articleId: null, productId: null, expiresAt: null };
			article.metrics = sumMetrics([article.metrics, { ...zeroMetrics(), clicks: 1 }]);
			return {
				ok: true,
				code: input.code,
				articleId: article.id,
				productId: article.productId,
				expiresAt: iso(Date.now() + UGC_ATTRIBUTION_DAYS * DAY),
			};
		},
		partner: {
			me: async () => {
				await delay();
				if (!signedIn()) throw new Error("Sign in required");
				return profile;
			},
			join: async (input) => {
				await delay();
				if (!signedIn()) throw new Error("Sign in required");
				profile = {
					partnerId: profile?.partnerId ?? "ptr_mock_1",
					userId: "usr_mock",
					role: input.role,
					displayName: input.displayName,
					channels: input.channels ?? [],
					active: true,
					createdAt: profile?.createdAt ?? iso(Date.now()),
				};
				return profile;
			},
			update: async (patch) => {
				await delay();
				const me = requirePartner();
				profile = { ...me, ...patch, channels: patch.channels ?? me.channels };
				return profile;
			},
			catalog: async (query = {}) => {
				await delay();
				requirePartner();
				const q = (query.q ?? "").trim().toLowerCase();
				let items: UgcCatalogItem[] = pool().map((p, index) => ({
					productId: p.id,
					productTitle: p.name,
					productImage: p.photos[0] ?? null,
					price: p.price,
					shopId: p.shopId,
					shopName: shopName(p.shopId),
					categoryName: p.category,
					commissionPercent: percentOf(index),
					access: accessOf(p.id, index),
					cr: Math.round(((index * 7) % 40) / 4) / 2,
					myArticles: articles.filter((a) => a.productId === p.id && a.status !== "deleted").length,
				}));
				if (q) items = items.filter((i) => `${i.productTitle} ${i.shopName}`.toLowerCase().includes(q));
				if (query.categoryId) {
					const ids = new Set(pool().filter((p) => p.categoryId === query.categoryId).map((p) => p.id));
					items = items.filter((i) => ids.has(i.productId));
				}
				if (query.minCommission) items = items.filter((i) => i.commissionPercent >= (query.minCommission ?? 0));
				const sort = query.sort ?? "commission";
				if (sort === "commission") items.sort((a, b) => b.commissionPercent - a.commissionPercent);
				if (sort === "cr") items.sort((a, b) => b.cr - a.cr);
				if (sort === "new") items.reverse();
				const pageSize = query.pageSize ?? 24;
				const page = query.page ?? 1;
				return { items: items.slice((page - 1) * pageSize, page * pageSize), total: items.length };
			},
			articles: async (query = {}) => {
				await delay();
				requirePartner();
				return articles.filter(
					(a) =>
						a.status !== "deleted" &&
						(!query.productId || a.productId === query.productId) &&
						(!query.status || a.status === query.status),
				);
			},
			createArticle: async (input) => {
				await delay(220);
				const { product, index } = productFor(input.productId);
				return makeArticle(product, index, input);
			},
			bulk: async (input) => {
				await delay(320);
				const { product, index } = productFor(input.productId);
				if (input.sources.length > 50) throw new Error("Не больше 50 источников за раз");
				return input.sources.map((s) =>
					makeArticle(product, index, {
						name: s.name,
						placement: s.placement ?? "",
						campaign: s.campaign ?? "",
						channel: (s.channel ?? "other") as UgcChannel,
					}),
				);
			},
			updateArticle: async (id, patch) => {
				await delay();
				requirePartner();
				const article = articles.find((a) => a.id === id);
				if (!article) throw new Error("Артикул не найден");
				if (article.status === "blocked") throw new Error("Артикул заблокирован площадкой");
				const status = patch.status === "active" || patch.status === "disabled" ? patch.status : article.status;
				Object.assign(article, {
					...(patch.name ? { name: patch.name } : {}),
					...(patch.placement != null ? { placement: patch.placement } : {}),
					...(patch.campaign != null ? { campaign: patch.campaign } : {}),
					...(patch.channel ? { channel: patch.channel } : {}),
					status,
					updatedAt: iso(Date.now()),
				});
				return { ...article };
			},
			requestAccess: async (productId, message) => {
				await delay();
				const me = requirePartner();
				const product = pool().find((p) => p.id === productId);
				if (!product) throw new Error("Товар не найден");
				access.set(productId, "pending");
				return {
					id: `req_${Math.random().toString(36).slice(2, 8)}`,
					productId,
					productTitle: product.name,
					shopId: product.shopId,
					shopName: shopName(product.shopId),
					partnerId: me.partnerId,
					partnerName: me.displayName,
					partnerRole: me.role,
					message: message ?? "",
					status: "pending",
					commissionPercent: null,
					reason: null,
					createdAt: iso(Date.now()),
					decidedAt: null,
				};
			},
			stats: async (query = {}) => {
				await delay();
				requirePartner();
				const days = query.days ?? 30;
				const to = Date.now();
				const from = to - days * DAY;
				const mine = articles.filter((a) => a.status !== "deleted");
				const rows = mine.map((a) => ({
					key: a.id,
					label: a.name,
					code: a.code,
					productTitle: a.productTitle,
					partnerName: a.partnerName,
					channel: a.channel,
					status: a.status,
					metrics: a.metrics,
				}));
				const totals = sumMetrics(mine.map((a) => a.metrics));
				const series = Array.from({ length: Math.min(days, 90) }, (_, i) => ({
					day: iso(from + (i + 1) * DAY).slice(0, 10),
					clicks: i === Math.min(days, 90) - 1 ? totals.clicks : 0,
					orders: i === Math.min(days, 90) - 1 ? totals.orders : 0,
					completedOrders: 0,
					commission: 0,
				}));
				return {
					from: iso(from),
					to: iso(to),
					groupBy: query.groupBy ?? "article",
					totals,
					rows,
					series,
					balance: { hold: 0, available: 0, paid: 0, cancelled: 0 },
				};
			},
			commissions: async () => {
				await delay();
				requirePartner();
				return [];
			},
		},
	};
};
